import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  AccountCircle,
  Schedule,
  VpnKey,
  Group,
  ContentCopy,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';
import PageLayout from '@/components/common/PageLayout';
import { useSnackbarNotification } from '@/hooks';
import { oAuthService } from '@/services/oAuthService';

interface DecodedToken {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
}

interface TokenClaims {
  aud?: string; // audience
  iss?: string; // issuer
  iat?: number; // issued at
  exp?: number; // expires at
  sub?: string; // subject
  name?: string; // user name
  preferred_username?: string; // username
  roles?: string[]; // user roles
  groups?: string[]; // user groups
  scp?: string; // scopes
  appid?: string; // application id
  tid?: string; // tenant id
  oid?: string; // object id
  [key: string]: unknown; // other claims
}

const TokenDebugger: React.FC = () => {
  const { accessToken, account, isAuthenticated, isLoading, acquireTokenSilently } = useAuth();
  const { snackbarOpen, snackbarMessage, snackbarType, closeSnackbar, showError, showSuccess } = useSnackbarNotification();
  const [decodedToken, setDecodedToken] = useState<DecodedToken | null>(null);
  const [tokenClaims, setTokenClaims] = useState<TokenClaims | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<number | null>(null);
  const [isTokenExpiring, setIsTokenExpiring] = useState(false);

  // Decode JWT token
  const decodeJWT = (token: string): DecodedToken | null => {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format');
      }

      // Decode header
      const header = JSON.parse(atob(parts[0].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Decode payload
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      return {
        header,
        payload,
        signature: parts[2]
      };
    } catch (err: unknown) {
      console.error('Error decoding JWT:', err);
      return null;
    }
  };

  // Format timestamp to readable date
  const formatTimestamp = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Check if token is expired
  const isTokenExpired = (exp: number): boolean => {
    return Date.now() >= exp * 1000;
  };

  // Format milliseconds to human readable string
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Handle manual token refresh
  const handleRefreshToken = async () => {
    setIsRefreshing(true);
    try {
      const newToken = await acquireTokenSilently();
      if (newToken) {
        showSuccess('Token refreshed successfully');
      } else {
        showError('Failed to refresh token');
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      showError('Failed to refresh token');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = (text: string) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    try {
      textArea.select();
      textArea.setSelectionRange(0, 99999); // For mobile devices
      const successful = document.execCommand('copy');
      if (!successful) {
        showError('Failed to copy to clipboard');
      }
    } catch {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
        }).catch(() => {
          showError('Failed to copy to clipboard');
        });
      } else {
        showError('Failed to copy to clipboard');
      }
    } finally {
      textArea.remove();
    }
  };

  // Decode token when accessToken changes
  useEffect(() => {
    if (accessToken) {
      const decoded = decodeJWT(accessToken);
      setDecodedToken(decoded);
      setTokenClaims(decoded?.payload || null);
    } else {
      setDecodedToken(null);
      setTokenClaims(null);
    }
  }, [accessToken]);

  // Update time until expiry and expiring status
  useEffect(() => {
    const updateTokenInfo = () => {
      const timeLeft = oAuthService.getTimeUntilExpiration();
      setTimeUntilExpiry(timeLeft);
      setIsTokenExpiring(oAuthService.isTokenExpiring());
    };

    // Update immediately
    updateTokenInfo();

    // Update every second
    const intervalId = setInterval(updateTokenInfo, 1000);

    return () => clearInterval(intervalId);
  }, [accessToken]);

  if (isLoading) {
    return (
      <PageLayout
        title="Token Debugger"
        notificationOpen={snackbarOpen}
        notificationMessage={snackbarMessage}
        notificationType={snackbarType}
        onNotificationClose={closeSnackbar}
      >
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading authentication information...
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <PageLayout
        title="Token Debugger"
        notificationOpen={snackbarOpen}
        notificationMessage={snackbarMessage}
        notificationType={snackbarType}
        onNotificationClose={closeSnackbar}
      >
        <Alert severity="warning" icon={<AccountCircle />}>
          <Typography variant="h6">Authentication Required</Typography>
          <Typography variant="body2">
            You need to be authenticated to view token information. Please sign in first.
          </Typography>
        </Alert>
      </PageLayout>
    );
  }

  if (!accessToken) {
    return (
      <PageLayout
        title="Token Debugger"
        notificationOpen={snackbarOpen}
        notificationMessage={snackbarMessage}
        notificationType={snackbarType}
        onNotificationClose={closeSnackbar}
      >
        <Alert severity="info" icon={<VpnKey />}>
          <Typography variant="h6">No Access Token Available</Typography>
          <Typography variant="body2">
            No access token is currently available. Please try signing in again.
          </Typography>
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Token Debugger"
      titleActions={
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            onClick={handleRefreshToken}
            startIcon={isRefreshing ? <CircularProgress size={20} /> : <Refresh />}
            disabled={isRefreshing || !isAuthenticated}
            size="medium"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh Token'}
          </Button>
          <Button
            variant="contained"
            onClick={() => copyToClipboard(accessToken ?? '')}
            startIcon={<ContentCopy />}
            size="medium"
            disabled={!accessToken}
          >
            Copy Token
          </Button>
        </Box>
      }
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
    >

      {/* Account Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" component="h2" display="flex" alignItems="center" mb={2}>
            <AccountCircle sx={{ mr: 1 }} />
            Account Information
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {account?.name && (
              <Chip label={`Name: ${account.name}`} variant="outlined" />
            )}
            {account?.username && (
              <Chip label={`Username: ${account.username}`} variant="outlined" />
            )}
            {account?.tenantId && (
              <Chip label={`Tenant: ${account.tenantId}`} variant="outlined" />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Token Status */}
      {tokenClaims && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" display="flex" alignItems="center" mb={2}>
              <Schedule sx={{ mr: 1 }} />
              Token Status
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {tokenClaims.exp && (
                <>
                  <Chip
                    label={`Expires: ${formatTimestamp(tokenClaims.exp)}`}
                    color={isTokenExpired(tokenClaims.exp) ? 'error' : 'success'}
                    variant="outlined"
                  />
                  {timeUntilExpiry !== null && !isTokenExpired(tokenClaims.exp) && (
                    <Chip
                      label={`Time Left: ${formatDuration(timeUntilExpiry)}`}
                      color={isTokenExpiring ? 'warning' : 'info'}
                      variant="filled"
                    />
                  )}
                  {isTokenExpiring && !isTokenExpired(tokenClaims.exp) && (
                    <Chip
                      label="Token Expiring Soon"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </>
              )}
              {tokenClaims.iat && (
                <Chip
                  label={`Issued: ${formatTimestamp(tokenClaims.iat)}`}
                  variant="outlined"
                />
              )}
              {oAuthService.isRefreshInProgress() && (
                <Chip
                  label="Refresh In Progress"
                  color="info"
                  icon={<CircularProgress size={16} />}
                />
              )}
            </Box>
            {isTokenExpiring && tokenClaims.exp && !isTokenExpired(tokenClaims.exp) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Token is expiring soon. The system will automatically refresh it, or you can manually refresh using the button above.
              </Alert>
            )}
            {tokenClaims.exp && isTokenExpired(tokenClaims.exp) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Token has expired. Please refresh the token or sign in again.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Roles and Permissions */}
      {tokenClaims && (tokenClaims.roles || tokenClaims.groups || tokenClaims.scp) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" display="flex" alignItems="center" mb={2}>
              <Group sx={{ mr: 1 }} />
              Roles and Permissions
            </Typography>
            
            {tokenClaims.roles && tokenClaims.roles.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle1" mb={1}>Application Roles:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {tokenClaims.roles.map((role) => (
                    <Chip key={role} label={role} color="primary" />
                  ))}
                </Box>
              </Box>
            )}

            {tokenClaims.groups && tokenClaims.groups.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle1" mb={1}>Groups:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {tokenClaims.groups.map((group) => (
                    <Chip key={group} label={group} color="secondary" />
                  ))}
                </Box>
              </Box>
            )}

            {tokenClaims.scp && (
              <Box mb={2}>
                <Typography variant="subtitle1" mb={1}>Scopes:</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {tokenClaims.scp.split(' ').map((scope) => (
                    <Chip key={scope} label={scope} variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {(!tokenClaims.roles || tokenClaims.roles.length === 0) && 
             (!tokenClaims.groups || tokenClaims.groups.length === 0) && 
             !tokenClaims.scp && (
              <Alert severity="warning">
                No roles, groups, or scopes found in the token. This may indicate that the user has limited permissions.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Token Details Accordion */}
      {decodedToken && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6">Token Details</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Token Header:</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => copyToClipboard(JSON.stringify(decodedToken.header, null, 2))}
                startIcon={<ContentCopy />}
              >
                Copy Header
              </Button>
            </Box>
            <pre style={{ margin: 0, overflow: 'auto' }}>
              {JSON.stringify(decodedToken.header, null, 2)}
            </pre>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Token Claims:</Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => copyToClipboard(JSON.stringify(tokenClaims, null, 2))}
                startIcon={<ContentCopy />}
              >
                Copy Claims
              </Button>
            </Box>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Claim</TableCell>
                    <TableCell>Value</TableCell>
                    <TableCell width="80">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tokenClaims && Object.entries(tokenClaims).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" fontWeight="bold">
                          {key}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {typeof value === 'object' ? (
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(value, null, 2)}
                          </pre>
                        ) : key === 'exp' || key === 'iat' ? (
                          `${formatTimestamp(value as number)} (${value})`
                        ) : (
                          String(value)
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => copyToClipboard(
                            typeof value === 'object' 
                              ? JSON.stringify(value, null, 2) 
                              : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
                                ? String(value)
                                : JSON.stringify(value),
                          )}
                          startIcon={<ContentCopy />}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Copy
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
    </PageLayout>
  );
};

export default TokenDebugger;
